// Client-side income event projection — the single source of truth used by
// every Dashboard mode (Pay Period, Next 30 Days, Month) so behavior stays
// consistent. Mirrors backend `forecast.project_incomes_in_window`.
//
// Given a list of income rows, produce every occurrence that lands inside
// [start, end], filtering out paychecks the user has already marked
// received. Recurring rows are expanded from their base `expected_date`
// (respecting `end_date`); one-time rows honor their own status.
//
// Returns an array of { income_id, source, amount, date, frequency }.
const FREQ_STEP = { weekly: 7, biweekly: 14, monthly: 30 };

const parseIso = (s) => {
  const d = new Date(s + "T00:00:00");
  d.setHours(0, 0, 0, 0);
  return d;
};
const isoOf = (d) => d.toISOString().slice(0, 10);

export function projectIncomeEvents(incomes, start, end) {
  // Materialized "received" occurrences — when a user marks a specific
  // pay-date received, the backend writes an unscheduled income entry for
  // that date. Skip the corresponding future occurrence so it doesn't
  // double-count.
  const receivedMarks = new Set(
    (incomes || [])
      .filter((i) => i.status === "received" && i.expected_date)
      .map((i) => `${(i.source || "").toLowerCase()}|${i.expected_date}`)
  );

  const out = [];
  for (const inc of incomes || []) {
    if (!inc.expected_date) continue;
    const step = FREQ_STEP[inc.frequency];
    const base = parseIso(inc.expected_date);
    if (!step) {
      // One-time / unscheduled — only include if still expected.
      if (inc.status !== "expected") continue;
      if (base < start || base > end) continue;
      out.push({
        income_id: inc.id,
        source: inc.source,
        amount: Number(inc.amount || 0),
        date: inc.expected_date,
        frequency: inc.frequency,
      });
      continue;
    }
    // Recurring — walk forward, ignoring base row's status (the base row's
    // status tracks the most recent reconciled paycheck, not the entire
    // future series).
    const endInc = inc.end_date ? parseIso(inc.end_date) : null;
    const skipDates = new Set(inc.skip_dates || []);
    let cur = new Date(base);
    while (cur < start) cur = new Date(cur.getTime() + step * 86400000);
    // Walk BACKWARD only if we're inside the window AND still after the
    // base — recurring occurrences never exist before the base date, so a
    // Nov-9 base must not project into Sep/Oct (bug 2026-02).
    while (cur > start && cur > base && new Date(cur.getTime() - step * 86400000) >= start) {
      cur = new Date(cur.getTime() - step * 86400000);
    }
    while (cur <= end) {
      if (endInc && cur > endInc) break;
      if (cur >= start) {
        const iso = isoOf(cur);
        const key = `${(inc.source || "").toLowerCase()}|${iso}`;
        if (!receivedMarks.has(key) && !skipDates.has(iso)) {
          const overrideAmt = inc.overrides && inc.overrides[iso];
          out.push({
            income_id: inc.id,
            source: inc.source,
            amount: Number(overrideAmt ?? inc.amount ?? 0),
            date: iso,
            frequency: inc.frequency,
          });
        }
      }
      cur = new Date(cur.getTime() + step * 86400000);
    }
  }
  // Dedupe overlapping series by (source|date).
  const seen = new Set();
  return out
    .sort((a, b) => a.date.localeCompare(b.date))
    .filter((e) => {
      const k = `${(e.source || "").toLowerCase()}|${e.date}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
}
